/** The hours of the day a run has, as a bar chart, and the hour the map shows.
 *  The range input is the control; a click on a bar moves it there. The bars are
 *  the run's vehicle miles, or, given `entries`, the entries into a charging zone:
 *  outlined with no charge, filled in the scenario chosen. */

import { hourSpan } from '../utils/trafficDataset';
import './DayStrip.css';

const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const whole = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const TICKS = [0, 6, 12, 18];

/** `entries`: { base: {hour: n}, scenario: {hour: n}, name } with name null for no
 *  charge; a charged hour the scenario has no result for has no scenario entry. */
function entriesText({ base, scenario, name }, hour, charged) {
  if (base[hour] === undefined) return null;
  const from = whole.format(base[hour]);
  if (name === null) return `${from} entries into the zone`;
  if (!charged) return `${from} entries into the zone · not charged`;
  if (scenario[hour] === undefined) return `${from} entries into the zone · ${name} not computed`;
  return `${from} → ${whole.format(scenario[hour])} entries into the zone`;
}

export default function DayStrip({ hours, value, onChange, lockNote, windows, entries = null }) {
  const byHour = new Map(hours.map((h) => [h.hour, h]));
  const charged = (h) => (windows || []).some(([a, b]) => a <= h && h < b);
  const max = entries
    ? Math.max(1, ...Object.values(entries.base), ...Object.values(entries.scenario))
    : Math.max(1, ...hours.map((h) => h.vmtMi || 0));
  const top = (v) => 1 - Math.max(0.04, (v || 0) / max);
  const at = byHour.get(value);
  const pick = (h) => {
    if (!lockNote && byHour.has(h) && h !== value) onChange(h);
  };
  const onBars = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    pick(Math.min(23, Math.max(0, Math.round(((e.clientX - r.left) / r.width) * 23))));
  };
  const bar = (className, hour, v, extra = {}) => (
    <rect className={className} x={hour - 0.36} y={top(v)} width={0.72} height={1 - top(v)} {...extra} />
  );
  return (
    <div className={`day-strip ${lockNote ? 'locked' : ''} ${entries ? 'day-strip-cp' : ''}`}>
      <div className="day-strip-head" aria-live="polite">
        <span className="day-strip-hour">{hourSpan(value)}</span>
        {entries ? (
          <span className="day-strip-stats">{entriesText(entries, value, charged(value))}</span>
        ) : at && (
          <span className="day-strip-stats">
            {compact.format(at.vehicles)} vehicles · {compact.format(at.vmtMi)} vehicle miles
          </span>
        )}
      </div>
      {lockNote && <div className="day-strip-lock">{lockNote}</div>}
      <svg className="day-strip-bars" viewBox="0 0 23 1" preserveAspectRatio="none"
        onClick={onBars} aria-hidden="true">
        {(windows || []).map(([a, b]) => (
          <rect key={a} className="day-strip-window" x={a - 0.5} y={0} width={b - a} height={1} />
        ))}
        {entries
          ? hours.map(({ hour }) => (
            <g key={hour} className={`day-strip-pair ${hour === value ? 'active' : ''}`}>
              {entries.scenario[hour] !== undefined && bar('day-strip-fill', hour, entries.scenario[hour])}
              {entries.base[hour] !== undefined
                && bar('day-strip-outline', hour, entries.base[hour], { vectorEffect: 'non-scaling-stroke' })}
            </g>
          ))
          : hours.map(({ hour, vmtMi }) => (
            <rect key={hour} className={`day-strip-bar ${hour === value ? 'active' : ''}`}
              x={hour - 0.36} y={top(vmtMi)} width={0.72} height={1 - top(vmtMi)} />
          ))}
      </svg>
      <input type="range" className="day-strip-range" min={0} max={23} step={1} value={value}
        disabled={!!lockNote} aria-label="Hour of the day" aria-valuetext={hourSpan(value)}
        onChange={(e) => pick(Number(e.target.value))} />
      <div className="day-strip-axis" aria-hidden="true">
        {TICKS.map((h) => <span key={h} style={{ left: `${(h / 23) * 100}%` }}>{String(h).padStart(2, '0')}</span>)}
        <span className="day-strip-end">24</span>
      </div>
      {(entries || (windows && windows.length > 0)) && (
        <div className="day-strip-key">
          {entries && (
            <>
              <span><span className="day-strip-key-outline" />no charge</span>
              {entries.name !== null && (
                <span className="day-strip-key-name"><span className="day-strip-key-fill" />{entries.name}</span>
              )}
              <span>entries into the zone each hour</span>
            </>
          )}
          {windows && windows.length > 0 && <span><span className="day-strip-key-swatch" />charged hours</span>}
        </div>
      )}
    </div>
  );
}
